import {
  DeviceMode,
  isComplexElement,
  isShellNode,
  isSlotElement,
  notEmpty,
  type NodeV2,
} from '@vedalib/editor'
import cn from 'classnames'
import React, { useCallback, useMemo } from 'react'

import { getParentNode } from 'components/Editor/EditorContext/selectors/block'
import KeyBindTag from 'components/Editor/components/KeyBindTag/KeyBindTag'
import DropdownMenu from 'components/uiKit/DropdownMenu'
import type { KeyIconsType } from 'components/uiKit/Icon'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import type { IMenuOption } from 'components/uiKit/Menu'
import { AppMode } from 'services/Store/Project/enums'
import { useProjectContext } from 'services/Store/Project/hooks'
import {
  getIsMaxZIndex,
  getIsMinZIndex,
  getIsInFlow,
  getNodeStyle,
  getIsGrow,
  getIsShrink,
  getIsFlexFlow,
  getIsHidden,
  getIsLocked,
} from 'services/Store/Project/selectors'
import type { Block, IBlockMode } from 'services/Store/Project/types'
import { getEnumLabel } from 'utils/enum'

import {
  onAddRowDown,
  onAddRowUp,
  onClone,
  onCopy,
  onCut,
  onDelete,
  onGrow,
  onHide,
  onLock,
  onSendBackward,
  onSendForward,
  onShowGrid,
  onShrink,
  onToggleFlexFlow,
  onToggleFlow,
  onUnwrap,
  onWrapButton,
  onWrapContainer,
} from '../../commands'
import { NodeV2CommandsUiEnum } from '../../enum'
import {
  isGrid as checkGrid,
  isAbsolute as checkAbsolute,
  isFlex as checkFlex,
  isRoot as checkIsRoot,
  isComplexElementItem,
} from '../../guards'
import * as s from './NodeV2CommandsMenu.module.scss'

const genNodeV2CommandOption = ({
  value,
  bind,
  disabled,
  icon,
  separated,
  preventClose,
  hint,
  active,
}: {
  value: NodeV2CommandsUiEnum
  bind?: React.ComponentProps<typeof KeyBindTag>['bind'] | null
  icon?: KeyIconsType
  disabled?: boolean
  separated?: boolean
  preventClose?: boolean
  hint?: boolean
  active?: boolean
}) => ({
  value,
  label: (
    <div className={cn(s.label, { [s.hint]: hint })}>
      <span className={s.text}>
        {icon && (
          <Icon className={cn(s.icon, { [s.selected]: active })} name={icon} size={KitSize.XS} />
        )}
        {getEnumLabel('NodeV2CommandsUiEnum', value)}
      </span>
      {bind && <KeyBindTag bind={bind} />}
    </div>
  ),
  disabled,
  separated,
  preventClose,
})

interface INodeV2CommandsMenuProps {
  mode: IBlockMode
  block: Block
  node: NodeV2 | null
  selected: NodeV2[] | NodeV2
  color?: 'grid' | 'flex' | 'absolute' | 'element'
}

const NodeV2CommandsMenu: React.FC<INodeV2CommandsMenuProps> = (props) => {
  const { node, block, selected, color = 'element', mode } = props
  const { deviceMode, editorMode } = mode
  const isDesktop = deviceMode === DeviceMode.desktop
  const isPro = editorMode === AppMode.pro

  const isMinZIndex = useProjectContext(getIsMinZIndex, block.uuid, selected)
  const isMaxZIndex = useProjectContext(getIsMaxZIndex, block.uuid, selected)
  const isInFlow = useProjectContext(getIsInFlow, selected)
  const isGrow = useProjectContext(getIsGrow, selected)
  const isFlexFlow = useProjectContext(getIsFlexFlow, selected)
  const isShrink = useProjectContext(getIsShrink, selected)
  const isHide = useProjectContext(getIsHidden, selected)
  const isLock = useProjectContext(getIsLocked, selected)
  const isRoot = checkIsRoot(block, node)
  const style = useProjectContext(getNodeStyle, block.uuid, node?.id)

  const isGrid = checkGrid(style)
  const isAbsolute = checkAbsolute(style)
  const isFlex = checkFlex(style)

  const nodes = Array.isArray(selected) ? selected : [selected]
  const canHide = nodes.every((node) => !isComplexElementItem(block, node))
  const canLock = nodes.every((node) => !node.elementId)
  const isSlot = useMemo(() => {
    const node = Array.isArray(selected) ? selected[0] : selected
    const parent = node ? getParentNode(node.id, block) : null
    const element = parent?.elementId ? block.elements[parent.elementId] : null
    return Boolean(isSlotElement(element) && element.value.item?.value)
  }, [block, selected])

  const canUnwrap = useMemo(() => {
    const node = Array.isArray(selected) ? selected[0] : selected
    const element = node?.elementId ? block.elements[node.elementId] : null
    const isComplex = isComplexElement(element)
    const isSlot = isSlotElement(element) && element.value.item?.value
    const isLayout = !isShellNode(node)
    if (!isRoot || isLayout || isComplex || isSlot) {
      return true
    }
    return false
  }, [isRoot, block, selected])

  const options = useMemo(
    () =>
      [
        isPro && isFlex
          ? genNodeV2CommandOption({
              value: NodeV2CommandsUiEnum.FLEX_FLOW,
              bind: 'shift+X',
              icon: 'absolute',
              active: isFlexFlow,
            })
          : null,
        /* isGrid || isAbsolute || isFlexFlow
          ? */ genNodeV2CommandOption({
          value: NodeV2CommandsUiEnum.BRING_FORWARD,
          bind: 'shift+]',
          disabled: isMaxZIndex,
          icon: 'forward',
        }),
        /*  : null */ /*   isGrid || isAbsolute || isFlexFlow
          ?  */ genNodeV2CommandOption({
          value: NodeV2CommandsUiEnum.SEND_BACKWARD,
          bind: 'shift+[',
          disabled: isMinZIndex,
          icon: 'backward',
        }),
        /*  : null */ isPro && isGrid
          ? genNodeV2CommandOption({
              value: NodeV2CommandsUiEnum.ELEMENT_IN_FLOW,
              bind: 'shift+F',
              icon: isInFlow ? 'inFlow' : 'outOfFlow',
              active: isInFlow,
            })
          : null,
        /* <---------------------------------------------------------------> */
        isGrid
          ? genNodeV2CommandOption({
              value: NodeV2CommandsUiEnum.ADD_ROW_ABOVE,
              bind: 'ctrl+up',
              separated: true,
            })
          : null,
        isGrid
          ? genNodeV2CommandOption({
              value: NodeV2CommandsUiEnum.ADD_ROW_BELOW,
              bind: 'ctrl+down',
            })
          : null,
        isPro && isFlex && !isFlexFlow
          ? genNodeV2CommandOption({
              value: NodeV2CommandsUiEnum.GROW,
              bind: 'shift+G',
              icon: 'otherFullScreen',
              active: isGrow,
            })
          : null,
        isPro && isFlex && !isFlexFlow
          ? genNodeV2CommandOption({
              value: NodeV2CommandsUiEnum.SHRINK,
              bind: 'shift+S',
              icon: 'otherSmallScreen',
              active: isShrink,
            })
          : null,
        /* <---------------------------------------------------------------> */
        isDesktop && !isSlot
          ? genNodeV2CommandOption({
              value: NodeV2CommandsUiEnum.CLONE,
              bind: isPro ? 'ctrl+D' : null,
              separated: true,
            })
          : null,
        isDesktop && isPro
          ? genNodeV2CommandOption({
              value: NodeV2CommandsUiEnum.COPY,
              bind: 'ctrl+C',
            })
          : null,
        isDesktop && isPro && !isSlot
          ? genNodeV2CommandOption({
              value: NodeV2CommandsUiEnum.CUT,
              bind: 'ctrl+X',
            })
          : null,
        isDesktop && !isSlot
          ? genNodeV2CommandOption({
              value: NodeV2CommandsUiEnum.DELETE,
              bind: isPro ? 'delete|backspace' : null,
            })
          : null,
        /* <---------------------------------------------------------------> */
        isPro && canHide && !isSlot
          ? genNodeV2CommandOption({
              value: NodeV2CommandsUiEnum.HIDE,
              bind: 'shift+H',
              separated: true,
              icon: isHide ? 'eyeCloseEye' : 'eyeOpenEye',
              active: isHide,
            })
          : null,
        isPro && canLock
          ? genNodeV2CommandOption({
              value: NodeV2CommandsUiEnum.LOCK,
              bind: 'shift+L',
              icon: isLock ? 'lock' : 'lockOff',
              active: isLock,
            })
          : null,
        /* <---------------------------------------------------------------> */
        isDesktop && isPro && !isSlot
          ? genNodeV2CommandOption({
              value: NodeV2CommandsUiEnum.WRAP_IN_CONTAINER,
              bind: 'shift+C',
              icon: 'flexRow',
              separated: true,
            })
          : null,
        /* <---------------------------------------------------------------> */
        isDesktop && isPro && !isSlot
          ? genNodeV2CommandOption({
              value: NodeV2CommandsUiEnum.WRAP_IN_BUTTON,
              bind: 'shift+B',
              icon: 'iconsOtherButton',
            })
          : null,
        /* <---------------------------------------------------------------> */
        canUnwrap && isDesktop && isPro
          ? genNodeV2CommandOption({
              value: NodeV2CommandsUiEnum.UNWRAP,
              bind: 'shift+U',
              icon: 'ungroup',
            })
          : null,
        /* <---------------------------------------------------------------> */
        !isSlot
          ? genNodeV2CommandOption({
              value: NodeV2CommandsUiEnum.MOVE,
              bind: 'up|right|down|left',
              preventClose: true,
              separated: true,
              hint: true,
            })
          : null,
        isAbsolute
          ? genNodeV2CommandOption({
              value: NodeV2CommandsUiEnum.MOVE10,
              bind: 'ctrl+|up|right|down|left',
              preventClose: true,
              separated: true,
              hint: true,
            })
          : null,
        genNodeV2CommandOption({
          value: NodeV2CommandsUiEnum.NAVIGATE,
          bind: 'shift+|up|right|down|left',
          preventClose: true,
          separated: true,
          hint: true,
        }),
      ].filter(notEmpty) satisfies IMenuOption<NodeV2CommandsUiEnum>[],
    [
      isDesktop,
      isPro,
      isGrid,
      isAbsolute,
      isMaxZIndex,
      isMinZIndex,
      isInFlow,
      isFlex,
      isFlexFlow,
      isGrow,
      isShrink,
      isHide,
      isLock,
      canHide,
      canLock,
      canUnwrap,
      isSlot,
    ],
  )

  const handleClick = useCallback((item: IMenuOption<NodeV2CommandsUiEnum>) => {
    switch (item.value) {
      case NodeV2CommandsUiEnum.SEND_BACKWARD:
        onSendBackward()
        break
      case NodeV2CommandsUiEnum.BRING_FORWARD:
        onSendForward()
        break
      case NodeV2CommandsUiEnum.ELEMENT_IN_FLOW:
        onToggleFlow()
        break
      case NodeV2CommandsUiEnum.FLEX_FLOW:
        onToggleFlexFlow()
        break
      case NodeV2CommandsUiEnum.GROW:
        onGrow()
        break
      case NodeV2CommandsUiEnum.SHRINK:
        onShrink()
        break
      case NodeV2CommandsUiEnum.ADD_ROW_ABOVE:
        onAddRowUp()
        break
      case NodeV2CommandsUiEnum.ADD_ROW_BELOW:
        onAddRowDown()
        break
      case NodeV2CommandsUiEnum.COPY:
        onCopy()
        break
      case NodeV2CommandsUiEnum.CUT:
        onCut()
        break
      case NodeV2CommandsUiEnum.DELETE:
        onDelete()
        break
      case NodeV2CommandsUiEnum.CLONE:
        onClone()
        break
      case NodeV2CommandsUiEnum.UNWRAP:
        onUnwrap()
        break
      case NodeV2CommandsUiEnum.WRAP_IN_CONTAINER:
        onWrapContainer()
        break
      case NodeV2CommandsUiEnum.WRAP_IN_BUTTON:
        onWrapButton()
        break
      case NodeV2CommandsUiEnum.SHOW_GRID:
        onShowGrid()
        break

      case NodeV2CommandsUiEnum.HIDE:
        onHide()
        break
      case NodeV2CommandsUiEnum.LOCK:
        onLock()
        break
    }
  }, [])

  return (
    <DropdownMenu
      name='nodeActions'
      onClick={handleClick}
      options={options}
      placement='bottom-start'
    >
      {({ open }) => (
        <Icon
          className={cn(s[color], { [s.active]: open })}
          name='iconsOtherMore'
          size={KitSize.XS}
        />
      )}
    </DropdownMenu>
  )
}

export default React.memo(NodeV2CommandsMenu)
